import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import SearchIcon from '@mui/icons-material/Search';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { HERO_LANGUAGE_OPTIONS } from '@/features/home/components/hero/hero.constants';
import { TravelDatePicker } from '@/shared/components/TravelDatePicker';
import { LiveCitySelect } from '@/features/destinations/components/LiveCitySelect';
import { searchDropdownMenuProps } from '@/shared/constants/dropdownMenuProps';

export interface SearchValues {
  city?: string;
  date?: string;
  language?: string;
}

interface GuideSearchBarProps {
  variant?: 'hero' | 'page';
  initialValues?: SearchValues;
  onSearch: (values: SearchValues) => void;
}

const today = new Date().toISOString().split('T')[0];

export function GuideSearchBar({
  variant = 'page',
  initialValues = {},
  onSearch,
}: GuideSearchBarProps) {
  const [city, setCity] = useState(initialValues.city ?? '');
  const [date, setDate] = useState(initialValues.date ?? '');
  const [language, setLanguage] = useState(initialValues.language ?? '');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setCity(initialValues.city ?? '');
    setDate(initialValues.date ?? '');
    setLanguage(initialValues.language ?? '');
  }, [initialValues.city, initialValues.date, initialValues.language]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch({
      city: city.trim() || undefined,
      date: date || undefined,
      language: language || undefined,
    });
  };

  const activeFiltersCount = [city, date, language].filter(Boolean).length;

  if (variant === 'hero') {
    return null;
  }

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'stretch' },
        }}
      >
        <SearchField icon={<PlaceOutlinedIcon />} label="Destination">
          <LiveCitySelect
            value={city}
            onChange={setCity}
            placeholder="Any destination"
            variant="page"
          />
        </SearchField>

        <Box
          sx={{
            width: { md: 1 },
            height: { xs: 1, md: 'auto' },
            bgcolor: 'divider',
            alignSelf: 'center',
            my: { xs: 0, md: 2 },
          }}
        />

        <SearchField icon={<CalendarMonthOutlinedIcon />} label="Travel Date">
          <TravelDatePicker
            value={date}
            onChange={setDate}
            minDate={today}
            placeholder="Select date"
            displaySx={{
              fontWeight: date ? 600 : 400,
              fontSize: '0.9375rem',
              color: date ? '#0F172A' : '#64748B',
            }}
          />
        </SearchField>

        <Box
          sx={{
            width: { md: 1 },
            height: { xs: 1, md: 'auto' },
            bgcolor: 'divider',
            alignSelf: 'center',
            my: { xs: 0, md: 2 },
          }}
        />

        <SearchField icon={<TranslateOutlinedIcon />} label="Language">
          <Select
            value={language}
            onChange={(e: SelectChangeEvent) => setLanguage(e.target.value)}
            variant="standard"
            disableUnderline
            displayEmpty
            renderValue={(val) => (
              <Typography
                sx={{
                  fontWeight: val ? 600 : 400,
                  fontSize: '0.9375rem',
                  color: val ? 'text.primary' : 'text.muted',
                }}
              >
                {val ? HERO_LANGUAGE_OPTIONS.find((o) => o.value === val)?.label : 'Any language'}
              </Typography>
            )}
            MenuProps={searchDropdownMenuProps}
            sx={{
              width: '100%',
              '& .MuiSelect-select': { p: '0 !important', pr: '0 !important' },
              '& .MuiSelect-icon': { display: 'none' },
            }}
          >
            {HERO_LANGUAGE_OPTIONS.map(({ value, label }) => (
              <MenuItem key={value || 'any'} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </SearchField>

        <Box
          sx={{
            p: { xs: 2, md: 1.5 },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<TuneOutlinedIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              borderColor: 'divider',
              color: 'text.secondary',
              px: 2,
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.50',
              },
            }}
          >
            Filters
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: '0.6875rem',
                  bgcolor: 'primary.main',
                  color: 'white',
                }}
              />
            )}
          </Button>

          <Button
            type="submit"
            variant="contained"
            startIcon={<SearchIcon />}
            sx={{
              flex: { xs: 1, md: 'none' },
              bgcolor: 'primary.main',
              px: { xs: 3, md: 3 },
              py: { xs: 1.5, md: 1.75 },
              borderRadius: 1,
              fontSize: '0.9375rem',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: { md: 120 },
              boxShadow: 'none',
              '&:hover': {
                bgcolor: 'primary.dark',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
              },
            }}
          >
            Search
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

interface SearchFieldProps {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  onClick?: () => void;
}

function SearchField({ icon, label, children, onClick }: SearchFieldProps) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        minWidth: { md: 160 },
        px: { xs: 2.5, md: 2.5 },
        py: { xs: 2, md: 2.5 },
        cursor: onClick ? 'pointer' : 'default',
        borderBottom: { xs: '1px solid', md: 'none' },
        borderColor: 'divider',
        transition: 'background-color 200ms ease',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        '&:last-of-type': {
          borderBottom: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Box
          sx={{
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            '& svg': { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Typography
          component="label"
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: 'text.secondary',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}
